import graphql__b2c6c8e22e6ec948bbe5 from "./__generated__/OutgoingInvoicesViewInvoicesQuery.graphql.ts";import * as React from "react";
import {useState, useEffect, useCallback} from "react";
import Panel from "./presentational/Panel";
import {usePreloadedQuery} from "react-relay/hooks";
import {graphql} from "react-relay";
import Headline from "./presentational/Headline";
import InvoiceList, {FilterOption} from "./InvoiceList";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import type {OutgoingInvoicesViewInvoicesQuery as OutgoingInvoicesViewInvoicesQueryType} from "./__generated__/OutgoingInvoicesViewInvoicesQuery.graphql";
import CreateInvoiceDialog from "./dialog/CreateInvoiceDialog";
import ActionButton from "./presentational/button/ActionButton";
import {faPlusCircle, faSearch, faSpinnerThird} from "@fortawesome/pro-regular-svg-icons";
import {useOutletContext} from "react-router";
import {useSearchParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useIntl} from "react-intl";

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type OutgoingInvoicesViewProps = ConnectedProps<typeof connector>;

type InvoicesOutletContext = {
    invoicesQuery: any
};

const OutgoingInvoicesView: React.FC<OutgoingInvoicesViewProps> = (props) => {
    const intl = useIntl();
    const {dispatchShowModal, dispatchHideModal} = props
    const {invoicesQuery} = useOutletContext<InvoicesOutletContext>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [inputValue, setInputValue] = useState(searchParams.get('search') || '');
    const [isSearching, setIsSearching] = useState(false);
    const [filter, setFilter] = useState<FilterOption>((searchParams.get('filter') as FilterOption) || 'active');

    const invoices = usePreloadedQuery<OutgoingInvoicesViewInvoicesQueryType>(
        graphql__b2c6c8e22e6ec948bbe5,
        invoicesQuery,
    );

    // Update URL when search term or filter changes
    const updateURL = useCallback((term: string, filterValue: FilterOption) => {
        const newParams = new URLSearchParams(searchParams);
        if (term.trim()) {
            newParams.set('search', term);
        } else {
            newParams.delete('search');
        }
        if (filterValue !== 'active') {
            newParams.set('filter', filterValue);
        } else {
            newParams.delete('filter');
        }
        setSearchParams(newParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const searchAPIDebounced = AwesomeDebouncePromise(
        (searchTerm: string) => {
            setSearchTerm(searchTerm);
            updateURL(searchTerm, filter);
        },
        500
    );

    const handleSearchTermChange = (value: string) => {
        setInputValue(value);
        searchAPIDebounced(value);
    };

    const handleFilterChange = (newFilter: FilterOption) => {
        setFilter(newFilter);
        updateURL(searchTerm, newFilter);
    };

    // Initialize search term and filter from URL changes (browser navigation)
    useEffect(() => {
        const urlSearchTerm = searchParams.get('search') || '';
        const urlFilter = (searchParams.get('filter') as FilterOption) || 'active';
        setSearchTerm(urlSearchTerm);
        setInputValue(urlSearchTerm);
        setFilter(urlFilter);
    }, [searchParams]);

    const showCreateInvoiceDialog = () => {
        dispatchShowModal({
            body: (<CreateInvoiceDialog handleCloseDialog={dispatchHideModal}/>)
        })
    };

    return (
        <Panel>
            <div className="px-6 pt-6 pb-4">
                <div className="md:flex md:items-center md:justify-between">
                    <div className="flex-1 min-w-0">
                        <Headline>{intl.formatMessage({id: 'view.invoices.headline'})}</Headline>
                    </div>
                    <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                        <ActionButton icon={faPlusCircle} title={intl.formatMessage({id: 'view.invoices.createButton'})} onClick={() => {
                            showCreateInvoiceDialog()
                        }}/>
                    </div>
                </div>
                <div className="mt-6 flex space-x-4">
                    <select
                        value={filter}
                        onChange={(e) => handleFilterChange(e.target.value as FilterOption)}
                        className="rounded-md border-gray-300 shadow-xs focus:ring-red-500 focus:border-red-500 sm:text-sm"
                    >
                        <option value="active">{intl.formatMessage({id: 'view.invoices.filter.active'})}</option>
                        <option value="overdue">{intl.formatMessage({id: 'view.invoices.filter.overdue'})}</option>
                        <option value="done">{intl.formatMessage({id: 'view.invoices.filter.done'})}</option>
                        <option value="all">{intl.formatMessage({id: 'view.invoices.filter.all'})}</option>
                    </select>
                    <div className="flex-1 min-w-0">
                        <label htmlFor="search" className="sr-only">{intl.formatMessage({id: 'view.invoices.searchLabel'})}</label>
                        <div className="relative rounded-md shadow-xs">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FontAwesomeIcon icon={faSearch} className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                            </div>
                            <input
                                autoFocus={true}
                                type="search"
                                value={inputValue}
                                className="focus:ring-red-500 focus:border-red-500 block w-full pl-10 pr-10 sm:text-sm border-gray-300 rounded-md"
                                placeholder={intl.formatMessage({id: 'view.invoices.searchPlaceholder'})}
                                onChange={e => handleSearchTermChange(e.target.value)}
                            />
                            {isSearching && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                    <FontAwesomeIcon icon={faSpinnerThird} spin className="h-4 w-4 text-gray-400" aria-hidden="true"/>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <InvoiceList
                queryRef={invoices}
                searchTerm={searchTerm}
                filter={filter}
                onLoadingChange={setIsSearching}
            />
        </Panel>
    )
}

export default connector(OutgoingInvoicesView);
